import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo } from "react";
import Label from "../../components/label";
import {
  TeamCompetitionExemptionReason,
  TeamCompetitionExemptionReasonTranslations,
  TeamCompetitionStatus,
  TeamCompetitionStatusColors,
  TeamCompetitionStatusTranslations,
} from "../../types/team_competitions.types";
import {
  TeamCategoryId,
  TeamCategoryTranslations,
} from "../../types/team_categories.types";
import CrudTable from "../../components/crud-table";
import { Button } from "../../components/button";
import { ArrowLeftIcon, ArrowUpTrayIcon } from "@heroicons/react/24/solid";
import { useCompetitions } from "../../context/CompetitionsContext";
import { TeamWDetailsAndStats } from "../../types/team.types";
import { TeamsAPI } from "../../APIs/teams.api";
import { useToast } from "../../components/toast";
import { TeamMemberWithDetails } from "../../types/team_members.types";
import {
  Gender,
  GenderColors,
  GenderTranslations,
} from "../../types/personal_info.types";
import { formatPhoneNumber } from "../../utils/format-phone-number.util";
import { parseBirthdateToLocalFormat } from "../../utils/date.utils";
import { Loading } from "../../components/loading";
import Modal from "../../components/modal";
import { EditableField } from "../../components/editable-field";
import {
  Check,
  CircleX,
  XCircleIcon,
  CircleCheck,
  Plus,
  UserPlus,
  ShieldCheck,
  Banknote,
} from "lucide-react";
import { TeamCompetitionsAPI } from "../../APIs/team_competitions.api";
import { CompetitionPricesAPI } from "../../APIs/competition_prices.api";
import { PaymentType } from "../../types/payments.types";
import { PaymentsAPI } from "../../APIs/payments.api";
import { SplitCUITField } from "../../components/cuit-field";
import { TeamMembersAPI } from "../../APIs/team_members.api";
import { DATE_TIME_SHORT_24H } from "../../utils/date-formats";

const tmColumns = [
  {
    key: "username" as keyof TeamMemberWithDetails,
    label: "Usuario",
  },
  {
    key: "personal_info.dni" as keyof TeamMemberWithDetails,
    label: "DNI",
    render: (value: string) => value || "-",
  },
  {
    key: "personal_info.first_name" as keyof TeamMemberWithDetails,
    label: "Nombre",
    render: (value: string) => value || "-",
  },
  {
    key: "personal_info.last_name" as keyof TeamMemberWithDetails,
    label: "Apellido",
    render: (value: string) => value || "-",
  },
  {
    key: "personal_info.primary_phone_number" as keyof TeamMemberWithDetails,
    label: "Teléfono",
    render: (value: string) => (value ? formatPhoneNumber(value) : "-"),
  },
  {
    key: "personal_info.emergency_contact_number" as keyof TeamMemberWithDetails,
    label: "Contacto de Emergencia",
    render: (value: string) => (value ? formatPhoneNumber(value) : "-"),
  },
  {
    key: "personal_info.email" as keyof TeamMemberWithDetails,
    label: "Email",
    render: (value: string) => value || "-",
  },
  {
    key: "personal_info.gender" as keyof TeamMemberWithDetails,
    label: "Género",
    render: (value: Gender) =>
      value ? (
        <Label color={GenderColors[value as Gender]}>
          {GenderTranslations[value]}
        </Label>
      ) : (
        "-"
      ),
  },
  {
    key: "personal_info.blood_type" as keyof TeamMemberWithDetails,
    label: "Grupo Sanguíneo",
    render: (value: string) => value || "-",
  },
  {
    key: "personal_info.birth_date" as keyof TeamMemberWithDetails,
    label: "Fecha de Nacimiento",
    render: (value: string) =>
      value ? parseBirthdateToLocalFormat(value) : "-",
  },
  {
    key: "is_helmsman" as keyof TeamMemberWithDetails,
    label: "Rol",
    render: (value: boolean) => {
      if (value === undefined || value === null) return "-";
      return value ? (
        <Label color="green">Timonel</Label>
      ) : (
        <Label color="blue">Pescador</Label>
      );
    },
  },
];

const paymentHistoryColumns = [
  {
    key: "id",
    label: "ID",
    render: (value: number) => (
      <span className="font-mono text-sm">{value}</span>
    ),
    hideOnMobile: true,
  },
  {
    key: "type",
    label: "Tipo",
    render: (value: string) => (
      <Label
        color={
          value === "mercadopago"
            ? "blue"
            : value === "transfer"
            ? "orange"
            : "green"
        }
      >
        {value === "mercadopago"
          ? "Mercado Pago"
          : value === "transfer"
          ? "Transferencia"
          : "Efectivo"}
      </Label>
    ),
  },
  {
    key: "status",
    label: "Estado",
    render: (value: string) => (
      <Label
        color={
          value === "approved"
            ? "green"
            : value === "pending"
            ? "yellow"
            : "red"
        }
      >
        {value === "approved"
          ? "Aprobado"
          : value === "pending"
          ? "Pendiente"
          : "Rechazado"}
      </Label>
    ),
  },
  {
    key: "amount",
    label: "Monto",
    render: (value: number) => (
      <span className="font-medium">
        ${parseFloat(String(value)).toLocaleString("es-AR")}
      </span>
    ),
  },
  {
    key: "date",
    label: "Fecha",
    render: (value: string) => (
      <span className="text-sm">
        {new Date(value).toLocaleDateString("es-AR", DATE_TIME_SHORT_24H)}
      </span>
    ),
  },
  {
    key: "updated_at",
    label: "Actualizado",
    render: (value: string) => (
      <span className="text-sm">
        {new Date(value).toLocaleDateString("es-AR", DATE_TIME_SHORT_24H)}
      </span>
    ),
  },
  {
    key: "details",
    label: "Detalles",
    render: (value: any, payment: any) => (
      <div className="text-sm">
        {payment.type === "transfer" && payment.transfer_status && (
          <div>
            <Label
              color={
                payment.transfer_status === "confirmed"
                  ? "green"
                  : payment.transfer_status === "rejected"
                  ? "red"
                  : "yellow"
              }
            >
              {payment.transfer_status === "confirmed"
                ? "Confirmada"
                : payment.transfer_status === "rejected"
                ? "Rechazada"
                : "Pendiente"}
            </Label>
            {payment.transfer_confirmed_at && (
              <div className="text-xs text-gray-500 mt-1">
                Confirmada:{" "}
                {new Date(payment.transfer_confirmed_at).toLocaleDateString(
                  "es-AR",
                  DATE_TIME_SHORT_24H
                )}
              </div>
            )}
            {payment.transfer_rejection_reason && (
              <div className="text-xs text-red-600 mt-1 break-words">
                {payment.transfer_rejection_reason}
              </div>
            )}
          </div>
        )}
        {payment.type === "cash" && payment.cash_received_by_username && (
          <div className="text-xs text-gray-600">
            Recibido por: {payment.cash_received_by_username}
          </div>
        )}
        {!payment.transfer_status && payment.type !== "cash" && (
          <span className="text-gray-400">-</span>
        )}
      </div>
    ),
  },
];

export function TeamView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { currentCompetitionId } = useCompetitions();
  const [teamData, setTeamData] = useState<TeamWDetailsAndStats | null>(null);
  const { addToast } = useToast();

  const [showExemptionModal, setShowExemptionModal] = useState(false);
  const [showRemoveExemptionModal, setShowRemoveExemptionModal] =
    useState(false);
  const [exemptionData, setExemptionData] = useState<{
    exemption_reason: TeamCompetitionExemptionReason | null;
    exemption_notes: string | null;
  }>({
    exemption_reason: null,
    exemption_notes: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [availablePrices, setAvailablePrices] = useState<any[]>([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [transferFormData, setTransferFormData] = useState({
    comp_price_id: 0,
    sender_first_name: "",
    sender_last_name: "",
    sender_cuit: "",
  });

  const [showCashModal, setShowCashModal] = useState(false);
  const [cashFormData, setCashFormData] = useState({
    comp_price_id: 0,
    payer_first_name: "",
    payer_last_name: "",
    payer_dni: "",
  });

  const handleCashFormChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLTextAreaElement;
    setCashFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCashPriceChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLSelectElement;
    setCashFormData((prev) => ({
      ...prev,
      comp_price_id: Number(target.value),
    }));
  };

  const handleOpenCashModal = async () => {
    setIsLoadingPrices(true);
    setShowCashModal(true);

    try {
      const response = await CompetitionPricesAPI.getCompetitionPrices(
        currentCompetitionId!,
        PaymentType.CASH
      );

      if (response.statusCode === 200 && response.data.length > 0) {
        setAvailablePrices(response.data);
        setCashFormData((prev) => ({
          ...prev,
          comp_price_id: response.data[0].id,
        }));
      } else {
        addToast({
          message: "No hay precios en efectivo disponibles",
          type: "error",
          location: "bottom-center",
        });
        setShowCashModal(false);
      }
    } catch (error) {
      addToast({
        message: "Error al cargar los precios disponibles",
        type: "error",
        location: "bottom-center",
      });
      setShowCashModal(false);
    } finally {
      setIsLoadingPrices(false);
    }
  };

  const handleCashSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !cashFormData.payer_first_name.trim() ||
      !cashFormData.payer_last_name.trim() ||
      !cashFormData.payer_dni.trim() ||
      !cashFormData.comp_price_id
    ) {
      addToast({
        message: "Todos los campos son obligatorios",
        type: "error",
        location: "bottom-center",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await PaymentsAPI.createCashPayment({
        payer_first_name: cashFormData.payer_first_name.trim(),
        payer_last_name: cashFormData.payer_last_name.trim(),
        payer_dni: cashFormData.payer_dni.trim(),
        comp_price_id: cashFormData.comp_price_id,
        team_id: teamData!.team.id,
      });

      if (response.statusCode === 201) {
        addToast({
          message: "Pago en efectivo registrado exitosamente",
          type: "success",
          location: "bottom-center",
        });

        fetchTeamData();

        setShowCashModal(false);
        setCashFormData({
          comp_price_id: 0,
          payer_first_name: "",
          payer_last_name: "",
          payer_dni: "",
        });
      } else {
        addToast({
          message: response.message || "Error al registrar el pago",
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al registrar el pago en efectivo",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeCashModal = () => {
    setShowCashModal(false);
    setCashFormData({
      comp_price_id: 0,
      payer_first_name: "",
      payer_last_name: "",
      payer_dni: "",
    });
    setAvailablePrices([]);
  };

  const [isTogglingFamily, setIsTogglingFamily] = useState(false);

  const isFamily = useMemo(() => {
    return (
      teamData?.categories?.some(
        (cat) => cat.category_id === TeamCategoryId.FAMILIA
      ) || false
    );
  }, [teamData?.categories]);

  const handleToggleFamilyCategory = async () => {
    if (!teamData?.team?.id) return;

    setIsTogglingFamily(true);
    try {
      const response = await TeamsAPI.adminUpdateTeamFamilyCategory(
        teamData.team.id,
        !isFamily
      );

      if (response.statusCode === 200) {
        addToast({
          message: isFamily
            ? "Categoría familia removida"
            : "Categoría familia agregada",
          type: "success",
          location: "bottom-center",
        });

        fetchTeamData();
      } else {
        addToast({
          message: response.message || "Error al actualizar categoría",
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al actualizar categoría familia",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setIsTogglingFamily(false);
    }
  };

  const handleDeleteMember = async (member: TeamMemberWithDetails) => {
    if (!member.user_id) return;

    try {
      const response = await TeamMembersAPI.removeTeamMember(member.user_id);

      if (response.statusCode === 200) {
        addToast({
          message: `${member.personal_info?.first_name} ${member.personal_info?.last_name} ha sido eliminado del equipo`,
          type: "success",
          location: "bottom-center",
        });

        fetchTeamData();
      } else {
        addToast({
          message: response.message || "Error al eliminar el miembro",
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al eliminar el miembro del equipo",
        type: "error",
        location: "bottom-center",
      });
    }
  };
  const handleOpenTransferModal = async () => {
    setIsLoadingPrices(true);
    setShowTransferModal(true);

    try {
      const response = await CompetitionPricesAPI.getCompetitionPrices(
        currentCompetitionId!,
        PaymentType.TRANSFER
      );

      if (response.statusCode === 200 && response.data.length > 0) {
        setAvailablePrices(response.data);
        setTransferFormData((prev) => ({
          ...prev,
          comp_price_id: response.data[0].id,
        }));
      } else {
        addToast({
          message: "No hay precios de transferencia disponibles",
          type: "error",
          location: "bottom-center",
        });
        setShowTransferModal(false);
      }
    } catch (error) {
      addToast({
        message: "Error al cargar los precios disponibles",
        type: "error",
        location: "bottom-center",
      });
      setShowTransferModal(false);
    } finally {
      setIsLoadingPrices(false);
    }
  };

  const handleTransferFormChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLTextAreaElement;
    setTransferFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLSelectElement;
    setTransferFormData((prev) => ({
      ...prev,
      comp_price_id: Number(target.value),
    }));
  };

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !transferFormData.sender_first_name.trim() ||
      !transferFormData.sender_last_name.trim() ||
      !transferFormData.sender_cuit.trim() ||
      !transferFormData.comp_price_id
    ) {
      addToast({
        message: "Todos los campos son obligatorios",
        type: "error",
        location: "bottom-center",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await PaymentsAPI.createTransferPaymentAdmin({
        comp_price_id: transferFormData.comp_price_id,
        sender_first_name: transferFormData.sender_first_name.trim(),
        sender_last_name: transferFormData.sender_last_name.trim(),
        sender_cuit: transferFormData.sender_cuit.trim(),
        team_id: teamData!.team.id,
      });

      if (response.statusCode === 201) {
        addToast({
          message: "Pago de transferencia registrado exitosamente",
          type: "success",
          location: "bottom-center",
        });

        fetchTeamData();

        setShowTransferModal(false);
        setTransferFormData({
          comp_price_id: 0,
          sender_first_name: "",
          sender_last_name: "",
          sender_cuit: "",
        });
      } else {
        addToast({
          message: response.message || "Error al registrar el pago",
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al registrar el pago de transferencia",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeTransferModal = () => {
    setShowTransferModal(false);
    setTransferFormData({
      comp_price_id: 0,
      sender_first_name: "",
      sender_last_name: "",
      sender_cuit: "",
    });
    setAvailablePrices([]);
  };

  const handleExemptionReasonChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setExemptionData((prev) => ({
      ...prev,
      exemption_reason: value as TeamCompetitionExemptionReason,
      exemption_notes:
        value === TeamCompetitionExemptionReason.Other
          ? prev.exemption_notes
          : null,
    }));
  };

  const handleExemptionNotesChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setExemptionData((prev) => ({
      ...prev,
      exemption_notes: value,
    }));
  };

  const fetchTeamData = useCallback(async () => {
    const teamId = Number(id);
    const competitionId = Number(currentCompetitionId);

    if (!teamId || !competitionId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await TeamsAPI.getOneTeamOfCompetition(
        teamId,
        competitionId
      );

      if (response.statusCode === 200) {
        setTeamData(response.data);
      } else {
        addToast({
          message: response.message,
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al obtener el equipo",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setLoading(false);
    }
  }, [id, currentCompetitionId, addToast]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return <Loading />;
  }

  if (!teamData) {
    return null;
  }

  const handleExempt = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { exemption_reason, exemption_notes } = exemptionData;

      if (!exemption_reason || exemption_reason.trim() === "") {
        addToast({
          message: "Por favor, rellene el motivo de la exención",
          type: "error",
          location: "bottom-center",
        });
        return;
      }

      const payload = {
        exemption_reason: exemption_reason as TeamCompetitionExemptionReason,
        exemption_notes:
          exemption_reason === TeamCompetitionExemptionReason.Other
            ? exemption_notes
            : null,
      };

      const response = await TeamCompetitionsAPI.exemptTeamCompetition(
        teamData.stats.team_competition_id,
        payload
      );

      if (response.statusCode === 200) {
        addToast({
          message: "La exención de pago se ha realizado correctamente",
          type: "success",
          location: "bottom-center",
        });
        fetchTeamData();

        setShowExemptionModal(false);
      } else {
        addToast({
          message: response.message,
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al realizar la exención de pago",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelExempt = async () => {
    try {
      const response = await TeamCompetitionsAPI.removeExemption(
        teamData.stats.team_competition_id
      );

      if (response.statusCode === 200) {
        fetchTeamData();
        addToast({
          message: "La exención de pago se ha cancelado correctamente",
          type: "success",
          location: "bottom-center",
        });
        setShowRemoveExemptionModal(false);
      } else {
        addToast({
          message: response.message,
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al cancelar la exención de pago",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container p-8">
      {/* Header */}
      <Button
        type="button"
        text="Volver"
        className="mb-2 text-gray-700 hover:text-gray-900 transition-colors rounded-lg bg-gray-100 px-4 py-2"
        icon={<ArrowLeftIcon className="w-5 h-5 mr-2" />}
        onClick={handleBack}
      />
      <div className="flex justify-between items-center border-b border-gray-300 pb-4 mb-2">
        <h1 className="text-2xl text-gray-700 font-bold">
          Detalles del Equipo
        </h1>
        <Label
          color={
            TeamCompetitionStatusColors[
              teamData.stats.status as TeamCompetitionStatus
            ]
          }
          fontSize="sm"
        >
          {
            TeamCompetitionStatusTranslations[
              teamData.stats.status as TeamCompetitionStatus
            ]
          }
        </Label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Team Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="border-b border-gray-200 pb-3 mb-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-800">
                Información del Equipo
              </h2>
              <div className="flex flex-wrap gap-2 sm:justify-end">
                <Button
                  type="button"
                  onClick={() =>
                    navigate(`/dashboard/teams/edit/${teamData.team.id}`)
                  }
                  variant="edit"
                  text=""
                  className="flex-shrink-0"
                />
                <Button
                  text={
                    isTogglingFamily
                      ? "Procesando..."
                      : isFamily
                      ? "Quitar Categoria Familia"
                      : "Familia"
                  }
                  icon={
                    isFamily ? (
                      <CircleX className="mr-1" />
                    ) : (
                      <CircleCheck className="mr-1" />
                    )
                  }
                  variant={isFamily ? "delete" : "create"}
                  className="whitespace-nowrap text-sm flex-shrink-0"
                  onClick={handleToggleFamilyCategory}
                  disabled={isTogglingFamily}
                />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex">
              <div className="w-36 text-gray-600 font-medium">Nombre:</div>
              <div>{teamData.team.name}</div>
            </div>
            <div className="flex">
              <div className="w-36 text-gray-600 font-medium">N° Equipo:</div>
              <div>{teamData.stats.team_number || "-"}</div>
            </div>
            <div className="flex">
              <div className="w-36 text-gray-600 font-medium">
                N° de Registro:
              </div>
              <div>{teamData.stats.registration_number}</div>
            </div>
            <div className="flex">
              <div className="w-36 text-gray-600 font-medium">
                Barra Pesquera:
              </div>
              <div>{teamData.institution.name}</div>
            </div>
            <div className="flex">
              <div className="w-36 text-gray-600 font-medium">Categorias:</div>
              <div className="flex flex-wrap">
                {teamData.categories.length > 0 ? (
                  teamData.categories.map((category) => (
                    <Label
                      color="orange"
                      className="mr-1"
                      key={category.category_id}
                    >
                      {
                        TeamCategoryTranslations[
                          category.category_id as TeamCategoryId
                        ]
                      }
                    </Label>
                  ))
                ) : (
                  <Label color="gray">-</Label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Boat Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="border-b border-gray-200 pb-3 mb-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-800">
                Información de la Embarcación
              </h2>
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {teamData.team.boat_id ? (
                  <Button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/dashboard/boats/edit/${teamData.boat.id}?teamId=${teamData.team.id}`
                      )
                    }
                    variant="edit"
                    text=""
                    className="flex-shrink-0"
                  />
                ) : (
                  <Button
                    text="Agregar Embarcación"
                    icon={<Plus />}
                    variant="create"
                    className="whitespace-nowrap text-sm flex-shrink-0"
                    onClick={() =>
                      navigate(
                        `/dashboard/boats/create?teamId=${teamData.team.id}`
                      )
                    }
                  />
                )}
              </div>
            </div>
          </div>
          {teamData.team.boat_id ? (
            <div className="space-y-3">
              <div className="flex">
                <div className="w-36 text-gray-600 font-medium">Nombre:</div>
                <div>{teamData.boat.name}</div>
              </div>
              <div className="flex">
                <div className="w-36 text-gray-600 font-medium">Matricula:</div>
                <div>{teamData.boat.registration_number}</div>
              </div>
              <div className="flex">
                <div className="w-36 text-gray-600 font-medium">Motor:</div>
                <div>
                  {teamData.boat.engine_brand} ({teamData.boat.hp} HP)
                </div>
              </div>
              <div className="flex">
                <div className="w-36 text-gray-600 font-medium">Radio:</div>
                <div>{teamData.boat.has_radio ? "Sí" : "No"}</div>
              </div>
              <div className="flex">
                <div className="w-36 text-gray-600 font-medium">Yate:</div>
                <div>{teamData.boat.is_yacht ? "Sí" : "No"}</div>
              </div>
            </div>
          ) : (
            <div className="text-gray-500 italic">
              Este equipo aún no tiene una embarcación registrada.
            </div>
          )}
        </div>
      </div>

      {/* Team Members */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="border-b border-gray-200 pb-3 mb-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-800">
              Miembros del Equipo ({teamData.members.length}/3)
            </h2>
            <div className="flex flex-wrap gap-2 sm:justify-end">
              {teamData.members.length < 3 && (
                <Button
                  text="Agregar Miembro"
                  icon={<UserPlus className="mr-1" />}
                  variant="create"
                  className="whitespace-nowrap text-sm flex-shrink-0"
                  onClick={() =>
                    navigate(
                      `/dashboard/team-members/add?teamId=${teamData.team.id}`
                    )
                  }
                />
              )}
            </div>
          </div>
        </div>
        {teamData.members.length > 0 ? (
          <div className="overflow-x-auto">
            <CrudTable
              data={teamData.members}
              columns={tmColumns}
              actions={{
                edit: {
                  label: "",
                  onClick: (member) =>
                    navigate(
                      `/dashboard/team-members/edit/${member.user_id}?teamId=${teamData.team.id}`
                    ),
                },
                delete: {
                  label: "",
                  onClick: (member) => handleDeleteMember(member),
                },
              }}
              deleteConfirmation={{
                titleField: "personal_info.first_name",
                titlePrefix: "Eliminar miembro:",
                message: (member) =>
                  `¿Está seguro que desea eliminar a ${member.personal_info?.first_name} ${member.personal_info?.last_name} del equipo?`,
                confirmLabel: "Eliminar",
                cancelLabel: "Cancelar",
              }}
            />
          </div>
        ) : (
          <p className="text-center text-gray-500 italic">
            Este equipo aún no tiene miembros registrados.
          </p>
        )}
      </div>

      {/* Payment Info */}
      <div className="bg-white rounded-2xl shadow p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-200 pb-4 mb-4 gap-3">
          <h2 className="text-xl font-semibold text-gray-800">
            Información de Pago
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            {teamData.stats.status === TeamCompetitionStatus.Registered &&
              teamData.stats.pending_payments.length === 0 &&
              teamData.stats.payment_status !== "pending" && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    text="Registrar Transferencia"
                    icon={<ArrowUpTrayIcon className="w-4 h-4 mr-1" />}
                    onClick={handleOpenTransferModal}
                    className="bg-orange-500 text-white hover:bg-orange-600 px-3 py-2 flex items-center font-semibold rounded transition text-sm whitespace-nowrap justify-center"
                  />
                  <Button
                    type="button"
                    text="Registrar Pago en Efectivo"
                    icon={<Banknote className="w-4 h-4 mr-1" />}
                    onClick={handleOpenCashModal}
                    className="bg-emerald-500 text-white hover:bg-emerald-600 px-3 py-2 flex items-center font-semibold rounded transition text-sm whitespace-nowrap justify-center"
                  />
                </div>
              )}
            {!teamData.stats.is_payment_exempt ? (
              teamData.stats.status !== TeamCompetitionStatus.Accepted &&
              teamData.stats.status !==
                TeamCompetitionStatus.PaymentInProgress &&
              teamData.stats.pending_payments.length === 0 &&
              teamData.stats.payment_status !== "pending" && (
                <Button
                  type="button"
                  text="Eximir de Pago"
                  icon={<ShieldCheck className="w-4 h-4 mr-1" />}
                  onClick={() => setShowExemptionModal(true)}
                  className="bg-purple-500 text-white hover:bg-purple-600 px-3 py-2 flex items-center font-semibold rounded transition text-sm whitespace-nowrap justify-center"
                />
              )
            ) : (
              <Button
                type="button"
                text="Remover Exención"
                variant="delete"
                icon={<XCircleIcon className="w-4 h-4 mr-1" />}
                onClick={() => setShowRemoveExemptionModal(true)}
                className="text-sm whitespace-nowrap justify-center"
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="text-gray-600 font-medium mr-2">
                Estado del Equipo:
              </div>
              <Label
                color={
                  TeamCompetitionStatusColors[
                    teamData.stats.status as TeamCompetitionStatus
                  ]
                }
              >
                {
                  TeamCompetitionStatusTranslations[
                    teamData.stats.status as TeamCompetitionStatus
                  ]
                }
              </Label>
            </div>

            <div className="flex items-center">
              <div className="text-gray-600 font-medium mr-2">
                Estado de Pago:
              </div>
              <Label
                color={
                  teamData.stats.payment_status === "paid"
                    ? "green"
                    : teamData.stats.payment_status === "exempt"
                    ? "blue"
                    : teamData.stats.payment_status === "pending"
                    ? "yellow"
                    : "red"
                }
              >
                {teamData.stats.payment_status === "paid"
                  ? "Pagado"
                  : teamData.stats.payment_status === "exempt"
                  ? "Exento"
                  : teamData.stats.payment_status === "pending"
                  ? "Pendiente"
                  : "No Pagado"}
              </Label>
            </div>

            <div className="flex items-center">
              <div className="text-gray-600 font-medium mr-2">
                Exento de pago:
              </div>
              <Label
                color={teamData.stats.is_payment_exempt ? "green" : "gray"}
              >
                {teamData.stats.is_payment_exempt ? "Sí" : "No"}
              </Label>
            </div>

            {teamData.stats.is_payment_exempt ? (
              <>
                <div className="flex items-center">
                  <div className="text-gray-600 font-medium mr-2">
                    Razón de exención:
                  </div>
                  <div>
                    {TeamCompetitionExemptionReasonTranslations[
                      teamData.stats
                        .exemption_reason as TeamCompetitionExemptionReason
                    ] || "-"}
                  </div>
                </div>

                {teamData.stats.exemption_reason ===
                  TeamCompetitionExemptionReason.Other && (
                  <div className="flex items-center">
                    <div className="text-gray-600 font-medium mr-2">
                      Nota de exención:
                    </div>
                    <div>{teamData.stats.exemption_notes || "-"}</div>
                  </div>
                )}
              </>
            ) : null}

            {teamData.stats.total_paid_amount > 0 && (
              <div className="flex items-center">
                <div className="text-gray-600 font-medium mr-2">
                  Total Pagado:
                </div>
                <div className="text-xl font-bold text-green-600">
                  ${" "}
                  {parseFloat(
                    String(teamData.stats.total_paid_amount)
                  ).toLocaleString("es-AR")}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {teamData.stats.latest_payment && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3">Último Pago</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Tipo:</span>
                    <Label
                      color={
                        teamData.stats.latest_payment.type === "mercadopago"
                          ? "blue"
                          : teamData.stats.latest_payment.type === "transfer"
                          ? "orange"
                          : "green"
                      }
                    >
                      {teamData.stats.latest_payment.type === "mercadopago"
                        ? "MercadoPago"
                        : teamData.stats.latest_payment.type === "transfer"
                        ? "Transferencia"
                        : "Efectivo"}
                    </Label>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Estado:</span>
                    <Label
                      color={
                        teamData.stats.latest_payment.status === "approved"
                          ? "green"
                          : teamData.stats.latest_payment.status === "pending"
                          ? "yellow"
                          : "red"
                      }
                    >
                      {teamData.stats.latest_payment.status === "approved"
                        ? "Aprobado"
                        : teamData.stats.latest_payment.status === "pending"
                        ? "Pendiente"
                        : "Rechazado"}
                    </Label>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Monto:</span>
                    <span className="font-medium">
                      ${" "}
                      {parseFloat(
                        String(teamData.stats.latest_payment.amount)
                      ).toLocaleString("es-AR")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Fecha:</span>
                    <span className="text-sm">
                      {new Date(
                        teamData.stats.latest_payment.date
                      ).toLocaleDateString("es-AR", DATE_TIME_SHORT_24H)}
                    </span>
                  </div>
                  {teamData.stats.latest_payment.type === "transfer" &&
                    teamData.stats.latest_payment.transfer_status && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">
                          Estado Transfer:
                        </span>
                        <Label
                          color={
                            teamData.stats.latest_payment.transfer_status ===
                            "confirmed"
                              ? "green"
                              : teamData.stats.latest_payment
                                  .transfer_status === "rejected"
                              ? "red"
                              : "yellow"
                          }
                        >
                          {teamData.stats.latest_payment.transfer_status ===
                          "confirmed"
                            ? "Confirmada"
                            : teamData.stats.latest_payment.transfer_status ===
                              "rejected"
                            ? "Rechazada"
                            : "Pendiente"}
                        </Label>
                      </div>
                    )}
                </div>
              </div>
            )}

            {teamData.stats.has_pending_payments &&
              teamData.stats.pending_payments.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-2">
                    Pagos Pendientes ({teamData.stats.pending_payments.length})
                  </h4>
                  {teamData.stats.pending_payments.map((payment, index) => (
                    <div
                      key={payment.id}
                      className="text-sm text-yellow-700 mb-1"
                    >
                      {payment.type === "mercadopago"
                        ? "MercadoPago"
                        : payment.type === "transfer"
                        ? "Transferencia"
                        : "Efectivo"}
                      : ${" "}
                      {parseFloat(String(payment.amount)).toLocaleString(
                        "es-AR"
                      )}
                      {payment.type === "transfer" &&
                        payment.transfer_status && (
                          <span className="ml-2">
                            (
                            {payment.transfer_status === "pending"
                              ? "Pendiente confirmación"
                              : payment.transfer_status}
                            )
                          </span>
                        )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>

        {teamData.stats.payment_history &&
          teamData.stats.payment_history.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Historial de Pagos
              </h3>
              <CrudTable
                data={teamData.stats.payment_history}
                columns={paymentHistoryColumns}
                mobileCardTitle="id"
              />
            </div>
          )}
      </div>

      {showTransferModal && (
        <Modal
          title="Registrar Pago por Transferencia"
          onClose={closeTransferModal}
        >
          <div className="p-4">
            <p className="mb-4 text-gray-600">
              Complete los datos de la transferencia realizada para este equipo.
            </p>

            {isLoadingPrices ? (
              <div className="flex justify-center py-8">
                <Loading />
              </div>
            ) : (
              <form onSubmit={handleTransferSubmit}>
                <div className="flex flex-col gap-y-4">
                  <EditableField
                    label="Precio de Inscripción:"
                    value={transferFormData.comp_price_id}
                    onChange={handlePriceChange}
                    required
                    type="select"
                    name="comp_price_id"
                    placeholder="Seleccionar precio"
                    options={availablePrices.map((price) => ({
                      key: String(price.id),
                      value: `$${parseFloat(price.price).toLocaleString(
                        "es-AR"
                      )}`,
                    }))}
                    autocomplete="off"
                  />

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700 mb-2">
                      <strong>Datos del Remitente:</strong>
                    </p>
                    <p className="text-xs text-blue-600">
                      Complete con los datos de la persona que realizó la
                      transferencia (puede ser diferente al titular de la cuenta
                      del equipo).
                    </p>
                  </div>

                  <EditableField
                    label="Nombre del Remitente:"
                    value={transferFormData.sender_first_name}
                    onChange={handleTransferFormChange}
                    required
                    type="text"
                    name="sender_first_name"
                    placeholder="Nombre de quien realizó la transferencia"
                    autocomplete="off"
                  />

                  <EditableField
                    label="Apellido del Remitente:"
                    value={transferFormData.sender_last_name}
                    onChange={handleTransferFormChange}
                    required
                    type="text"
                    name="sender_last_name"
                    placeholder="Apellido de quien realizó la transferencia"
                    autocomplete="off"
                  />

                  <SplitCUITField
                    label="CUIT del Remitente"
                    value={transferFormData.sender_cuit}
                    onChange={handleTransferFormChange}
                    required
                    name="sender_cuit"
                  />
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    type="button"
                    text="Cancelar"
                    variant="cancel"
                    onClick={closeTransferModal}
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    text="Registrar Transferencia"
                    variant="create"
                    icon={<Check className="w-4 h-4 mr-2" />}
                    disabled={isSubmitting}
                  />
                </div>
              </form>
            )}
          </div>
        </Modal>
      )}

      {showCashModal && (
        <Modal title="Registrar Pago en Efectivo" onClose={closeCashModal}>
          <div className="p-4">
            <p className="mb-4 text-gray-600">
              Complete los datos del pago en efectivo realizado para este
              equipo.
            </p>

            {isLoadingPrices ? (
              <div className="flex justify-center py-8">
                <Loading />
              </div>
            ) : (
              <form onSubmit={handleCashSubmit}>
                <div className="flex flex-col gap-y-4">
                  <EditableField
                    label="Precio de Inscripción:"
                    value={cashFormData.comp_price_id}
                    onChange={handleCashPriceChange}
                    required
                    type="select"
                    name="comp_price_id"
                    placeholder="Seleccionar precio"
                    options={availablePrices.map((price) => ({
                      key: String(price.id),
                      value: `$${parseFloat(price.price).toLocaleString(
                        "es-AR"
                      )}`,
                    }))}
                    autocomplete="off"
                  />

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-700 mb-2">
                      <strong>Datos del Pagador:</strong>
                    </p>
                    <p className="text-xs text-green-600">
                      Complete con los datos de la persona que realizó el pago
                      en efectivo.
                    </p>
                  </div>

                  <EditableField
                    label="Nombre del Pagador:"
                    value={cashFormData.payer_first_name}
                    onChange={handleCashFormChange}
                    required
                    type="text"
                    name="payer_first_name"
                    placeholder="Nombre de quien realizó el pago"
                    autocomplete="off"
                  />

                  <EditableField
                    label="Apellido del Pagador:"
                    value={cashFormData.payer_last_name}
                    onChange={handleCashFormChange}
                    required
                    type="text"
                    name="payer_last_name"
                    placeholder="Apellido de quien realizó el pago"
                    autocomplete="off"
                  />

                  <EditableField
                    label="DNI del Pagador:"
                    value={cashFormData.payer_dni}
                    onChange={handleCashFormChange}
                    required
                    type="text"
                    name="payer_dni"
                    placeholder="DNI de quien realizó el pago"
                    autocomplete="off"
                  />
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    type="button"
                    text="Cancelar"
                    variant="cancel"
                    onClick={closeCashModal}
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    text="Registrar Pago en Efectivo"
                    variant="create"
                    icon={<Check className="w-4 h-4 mr-2" />}
                    disabled={isSubmitting}
                  />
                </div>
              </form>
            )}
          </div>
        </Modal>
      )}

      {/* Exemption Modal */}
      {showExemptionModal && (
        <Modal
          title="Confirmar Exención de Pago"
          onClose={() => setShowExemptionModal(false)}
        >
          <div className="p-4">
            <p className="mb-4">
              ¿Está seguro que desea eximir de pago a este equipo? Esta acción:
            </p>
            <ul className="list-disc ml-5 mb-4">
              <li>Eximirá al equipo de pagar la inscripción</li>
              <li>Le asignará un número de equipo</li>
            </ul>

            <form onSubmit={handleExempt}>
              <div className="flex flex-col gap-y-2">
                <EditableField
                  label="Razón de exención:"
                  value={exemptionData.exemption_reason}
                  onChange={handleExemptionReasonChange as any}
                  required
                  type="select"
                  name="exemption_reason"
                  placeholder="Indique la razón de la exención"
                  options={Object.entries(
                    TeamCompetitionExemptionReasonTranslations
                  ).map(([key, value]) => ({
                    key: String(key),
                    value,
                  }))}
                  autocomplete="off"
                />
                {exemptionData.exemption_reason ===
                  TeamCompetitionExemptionReason.Other && (
                  <EditableField
                    label="Notas de exención:"
                    value={exemptionData.exemption_notes}
                    onChange={handleExemptionNotesChange as any}
                    type="textarea"
                    name="exemption_notes"
                    minLength={4}
                    maxLength={250}
                    placeholder="Indique las notas de la exención"
                    autocomplete="off"
                    required
                  />
                )}
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <Button
                  type="button"
                  text="Cancelar"
                  variant="cancel"
                  onClick={() => setShowExemptionModal(false)}
                />
                <Button
                  type="submit"
                  text="Confirmar Exención"
                  variant="create"
                  icon={<Check className="w-4 h-4 mr-2" />}
                  disabled={isSubmitting}
                />
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Remove Exemption Modal */}
      {showRemoveExemptionModal && (
        <Modal
          title="Confirmar Remoción de Exención"
          onClose={() => setShowRemoveExemptionModal(false)}
        >
          <div className="p-4">
            <p className="mb-4">
              ¿Está seguro que desea remover la exención de pago de este equipo?
              Esta acción:
            </p>
            <ul className="list-disc ml-5 mb-4">
              <li>Requerirá que el equipo pague su inscripción</li>
              <li>Eliminará el número de equipo asignado</li>
            </ul>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                text="Cancelar"
                variant="cancel"
                disabled={isSubmitting}
                onClick={() => setShowRemoveExemptionModal(false)}
              />
              <Button
                type="button"
                text="Confirmar Remoción"
                variant="create"
                disabled={isSubmitting}
                onClick={() => handleCancelExempt()}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
